import Component from 'components/component';
import { IsVisible } from 'components/is-visible';
import { ScreenLink } from 'components/screen-link';
import { Widget } from 'components/widget';
import { BaseResponseFailure } from 'controllers/controller';
import { Errors } from 'errors';
import translation from 'helpers/translation';
import debounce = require('lodash/debounce');

export abstract class Screen extends Component {
  public readonly containerEl: HTMLElement;
  public readonly context: ScreenContext;
  protected isSwitched: boolean;
  protected disableInputOnSuccessfulSubmit: boolean;
  protected dialogEl: HTMLElement | undefined;
  protected formEl: HTMLElement | undefined;
  protected errorEl?: HTMLElement;
  protected widgets: Array<Widget>;
  protected errorCount: number;
  protected captchaWidgetID: number | undefined;
  protected maxFailedLoginAttempts: number;

  constructor(containerEl: HTMLElement, context: ScreenContext, dialog?: HTMLElement) {
    super();

    this.containerEl = containerEl;
    this.context = context;
    this.dialogEl = dialog;
    this.disableInputOnSuccessfulSubmit = false;
    this.errorCount = 0;
    this.maxFailedLoginAttempts = 3;

    if (this.dialogEl) {
      const closeButton = this.dialogEl.querySelector('#msso-dialog-close') as HTMLElement;
      closeButton.onclick = () => {
        this.close();
      };
    }
  }

  public abstract renderImpl(): Promise<void>;

  public async submitImpl(): Promise<void> {}

  public async submit(): Promise<void> {
    // Hide all errors on submit.
    this.hideError();

    // Disable all inputs.
    const inputs = this.containerEl.querySelectorAll('input');
    for (let i = 0; i < inputs.length; i++) {
      if (!inputs.item(i).disabled) {
        inputs.item(i).disabled = true;
        inputs.item(i).setAttribute('data-temporary-disabled', 'true');
      }
    }
    let e: BaseResponseFailure | undefined = undefined;
    // Submit form.
    try {
      this.handleCaptchaVerification();
      await this.submitImpl();
      // Go to next screen or close if not set.
      const screenEl = this.containerEl.querySelector('.msso-screen') as HTMLDivElement;
      const nextScreen = screenEl.getAttribute('data-next-screen');
      if (nextScreen) {
        this.switchScreen(nextScreen);
      } else {
        this.close();
      }
      this.resetCaptcha();
    } catch (ex) {
      this.errorCount++;
      e = ex;
      if (e && e.errorMessage === Errors[Errors.TRIGGER_PASSWORD_CHANGE]) {
        this.switchScreen('reset-password', {
          source: 'triggerPasswordChange',
          passwordResetToken: e.errorDetails.passwordResetToken,
        });
      } else {
        this.showError((e && e.errorMessage) || Errors[Errors.UNKNOWN_ERROR]);
      }
      await this.showCaptchaWidget();
    }

    // Re-enable all inputs if there is an error or
    // if disableInputOnSuccessfulSubmit is not true.
    if (e || !this.disableInputOnSuccessfulSubmit) {
      for (let i = 0; i < inputs.length; i++) {
        if (inputs.item(i).getAttribute('data-temporary-disabled') === 'true') {
          inputs.item(i).disabled = false;
          inputs.item(i).removeAttribute('data-temporary-disabled');
        }
      }
    }
  }

  public close(): void {
    const dialog = document.getElementById('msso-dialog');
    if (dialog) {
      dialog.remove();
    }
  }

  public async render(): Promise<void> {
    // Empty container to ensure any previous elements can clean up after themselves.
    while (this.containerEl.firstChild) {
      this.containerEl.removeChild(this.containerEl.firstChild);
    }

    // Render screen.
    await this.renderImpl();
    if (this.isSwitched) {
      return;
    }

    // Look for widgets to render.
    this.widgets = [];
    const widgetConstructors = [ScreenLink, IsVisible];
    for (const widgetConstructor of widgetConstructors) {
      const els = this.containerEl.querySelectorAll(widgetConstructor.selector);
      for (let i = 0; i < els.length; i++) {
        this.widgets.push(new widgetConstructor(this, els.item(i) as any));
      }
    }
    if (this.dialogEl) {
      const titleEl = this.dialogEl.querySelector('#msso-dialog-title') as HTMLElement;
      titleEl.innerHTML = this.getTitle();
    }

    this.formEl = this.containerEl.querySelector('form') as HTMLFormElement | undefined;
    if (this.formEl) {
      this.formEl.onsubmit = () => {
        // Trigger submit.
        this.submit();

        // Cancel default submit event.
        return false;
      };

      // Find the first form element and focus.
      const firstEl = this.formEl.querySelector('input,select,textarea') as HTMLElement | undefined;
      if (firstEl && typeof firstEl?.focus === 'function') {
        firstEl.focus();
      }

      // Identify and cache the error containerEl.
      this.errorEl = this.containerEl.querySelector('.msso-error') as HTMLElement | undefined;
    }
  }

  public switchScreen(screenID: string, context?: ScreenContext): void {
    this.isSwitched = true;
    this.marinSSO.renderScreen(
      {
        screenID,
        container: this.containerEl,
      },
      context || this.context
    );
  }

  protected abstract getTitle(): string;

  protected hideError(): void {
    // Reset error containerEl.
    if (this.errorEl) {
      this.errorEl.innerHTML = '';
      this.errorEl.style.display = 'none';
    }
  }

  protected showError(errorMessage: string): void {
    if (this.errorEl) {
      this.errorEl.innerHTML = translation(errorMessage);
      this.errorEl.style.display = 'block';
    }
  }

  protected getFormInput(name: string): HTMLInputElement {
    return this.containerEl.querySelector(`input[name="${name}"]`) as HTMLInputElement;
  }

  protected getFormDropdownInput(name: string): HTMLInputElement {
    return document.getElementById(name) as HTMLInputElement;
  }

  protected removeAllDropdownOptions(selectElement: HTMLSelectElement) {
    while (selectElement.options.length > 0) {
      selectElement.remove(0);
    }
  }

  protected getFormValue(name: string): string {
    return this.getFormInput(name).value;
  }

  protected toggleFormElement(name: string, visible: boolean): void {
    if (this.containerEl) {
      const domElement = this.containerEl.querySelector(`.${name}`) as HTMLElement;
      if (domElement) {
        domElement.style.visibility = visible ? 'visible' : 'hidden';
      }
    }
  }

  protected getSelectedDropDownValue(name: string): string {
    return this.getDropDownOption(name)?.value;
  }

  protected getDropDownOption(name: string) {
    const select: any = this.getFormDropdownInput(name);
    const option = select.options[select.selectedIndex];
    return option;
  }

  protected onFormValueChange(name: string, callback: (value: string) => any): void {
    // Get elements.
    const el = this.getFormInput(name);

    // Set initial value, used to detect changes.
    let lastKnownValue = this.getFormValue(name);

    // Debounced function to call after a change is made.
    const onChangeDebounced = debounce(() => {
      callback(lastKnownValue);
    }, 300);

    // Called every time a possible change is made.
    const onPossibleChange = () => {
      const value = this.getFormValue(name);
      if (lastKnownValue !== value) {
        lastKnownValue = value;
        onChangeDebounced();
      }
    };

    // Bind to event handlers and poll for changes.
    // Bindings handle change instantly, and polling catches auto-fill and similar changes.
    el.onkeyup = onPossibleChange;
    el.onchange = onPossibleChange;
    const i = setInterval(() => {
      if (!el?.ownerDocument?.contains(el)) {
        clearInterval(i);
        return;
      }
      onPossibleChange();
    }, 300);
  }

  /**
   * Emit debounc value on dropdown select changed, refer onFormValueChange() above
   */
  protected onDropDownFormValueChange(name: string, callback: (value: string) => any): void {
    // Get elements.
    const el: any = document.getElementById(name);
    const option = el.options[el.selectedIndex];

    // Set initial value, used to detect changes.
    let lastKnownValue = option.value;

    // Debounced function to call after a change is made.
    const onChangeDebounced = debounce(() => {
      callback(lastKnownValue);
    }, 300);

    // Called every time a possible change is made.
    const onPossibleChange = () => {
      const value = this.getSelectedDropDownValue(name);
      if (lastKnownValue !== value) {
        lastKnownValue = value;
        onChangeDebounced();
      }
    };

    // Bind to event handlers and poll for changes.
    // Bindings handle change instantly, and polling catches auto-fill and similar changes.
    el.onkeyup = onPossibleChange;
    el.onchange = onPossibleChange;
    const i = setInterval(() => {
      if (!el?.ownerDocument?.contains(el)) {
        clearInterval(i);
        return;
      }
      onPossibleChange();
    }, 300);
  }

  /**
   * Show captcha when there are 3 invalid attempts
   */
  protected shouldRenderCaptcha(): boolean {
    return this.errorCount >= this.maxFailedLoginAttempts;
  }

  /**
   * Should handle verification after captcha
   */
  private handleCaptchaVerification(): void {
    // Verify captcha if that is present on the page
    if (this.shouldRenderCaptcha() && this.captchaWidgetID !== undefined) {
      const token = window.grecaptcha.getResponse(this.captchaWidgetID);
      // If token is undefined that then throw error
      if (!token) {
        const response: BaseResponseFailure = {
          success: false,
          errorMessage: Errors[Errors.INVALID_CAPTCHA],
          errorDetails: undefined,
        };
        throw response;
      }
    }
  }

  /**
   * Show captcha
   */
  protected async showCaptchaWidget(): Promise<void> {
    // Show captcha when there is invalid attempts
    if (this.shouldRenderCaptcha() && this.captchaWidgetID === undefined) {
      // Add a timeout since lib is loaded asynchrnonously from google
      setTimeout(async () => {
        this.captchaWidgetID = await window.grecaptcha.render('captcha', {
          theme: 'light',
          sitekey: '6LfN9aceAAAAAJ64BQXlyivaI9A4SMtm4caYN5if',
        });
      }, 3000);
    }
  }

  /**
   * Reset captcha
   */
  private resetCaptcha(): void {
    this.errorCount = 0;
    this.captchaWidgetID = undefined;
  }
}

export type ScreenContext = any;

export type ScreenContextTriggerPasswordChange = {
  source: 'triggerPasswordChange' | 'passwordResetTokenQueryString';
  passwordResetToken: string;
};

export type ScreenContextTriggerIdentityVerification = {
  source: 'triggerIdentityVerification';
  site: {
    siteID: string;
    name: string;
  };
  loginID: string;
};

export interface ScreenStatic {
  screenID: string;
  new (container: HTMLElement, context: ScreenContext, dialog?: HTMLElement): Screen;
}
